import type { ProductIdsWithVariantIds } from "replo-runtime/shared/types";
import type { ReploComponent } from "schemas/component";
import type { ProductRef } from "schemas/product";

import * as React from "react";

import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import SelectableButtons from "@v1/components/designSystem/selectable-buttons/SelectableButtons";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import capitalize from "lodash-es/capitalize";
import { pluralize } from "replo-utils/lib/pluralize";

import {
  ProductSelectionButton,
  ProductSelectionCombobox,
} from "../page/ProductSelectionCombobox";
import { ProductVariantSelectionCombobox } from "../page/ProductVariantSelectionCombobox";

const BundleProductsCustomPropModifier = ({
  component,
}: {
  component: ReploComponent;
}) => {
  const applyComponentAction = useApplyComponentAction();
  const bundleTargetType =
    (component.props.bundleTargetType as string) ?? "products";
  const productsWithVariantsArray = component.props.productsWithVariants ?? [];
  const productsWithVariants = productsWithVariantsArray.reduce(
    (productsObject: ProductIdsWithVariantIds, { productId, variantIds }) => {
      productsObject[productId] = variantIds;
      return productsObject;
    },
    {},
  );

  const handleToggleTargetType = (newTargetType: string) => {
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        bundleTargetType: newTargetType,
      },
    });
  };
  const handleUpdateProductsWithVariants = (
    productsWithVariants: ProductIdsWithVariantIds | null,
  ) => {
    const productsWithVariantsArray = Object.entries(
      productsWithVariants ?? {},
    ).map(([productId, variantIds]) => ({ productId, variantIds }));
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        productsWithVariants: productsWithVariantsArray,
      },
    });
  };

  const handleUpdateProducts = (productRefs: ProductRef[]) => {
    const newProductsWithVariants = productRefs.reduce(
      (productsObject: ProductIdsWithVariantIds, productRef) => {
        const productId = String(productRef.productId);
        productsObject[productId] = productsWithVariants[productId] ?? null;
        return productsObject;
      },
      {},
    );
    handleUpdateProductsWithVariants(newProductsWithVariants);
  };

  const productRefs = Object.keys(productsWithVariants).map((id: string) => ({
    productId: id,
  }));
  return (
    <>
      <DeprecatedLabeledControl size="sm" label="Bundle is made of">
        <SelectableButtons
          options={[
            { label: "Products", value: "products" },
            { label: "Variants", value: "variants" },
          ]}
          onChange={(value: string[]) => handleToggleTargetType(value[0]!)}
          value={[bundleTargetType]}
          size="sm"
          multiSelect={false}
        />
      </DeprecatedLabeledControl>
      <DeprecatedLabeledControl
        size="sm"
        label={`${capitalize(bundleTargetType)} in bundle`}
        description={`Define the ${bundleTargetType} users choose from`}
      >
        {bundleTargetType === "products" ? (
          <ProductSelectionCombobox
            value={productRefs}
            onChange={handleUpdateProducts}
            isMultiselect
            side="right"
            trigger={
              <ProductSelectionButton
                size="base"
                title={`${productRefs.length} ${pluralize({ singular: "product", plural: "products", count: productRefs.length })}`}
              />
            }
          />
        ) : (
          <ProductVariantSelectionCombobox
            align="end"
            value={productsWithVariants}
            onSubmit={handleUpdateProductsWithVariants}
          />
        )}
      </DeprecatedLabeledControl>
    </>
  );
};

export default BundleProductsCustomPropModifier;
